import React from 'react';

import AutoSizer from 'components/AutoSizer';
import {BLOCK_EDITOR_TOP_ROW_HEIGHT} from 'insights/InsightsConstants';
import SQLEditor from 'insights/warehouse/sql-editor/SQLEditor';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Pane from 'ui-library/pane/Pane';

interface Actions {
  saveAndRunQuery: () => Promise<void>;
  setSQLQuery: (newSQLQuery: string | null) => void;
  setSQLSubQuery: (sqlQuerySelection: string | null) => void;
}

export const SQL_EDITOR_ARIA_LABEL = 'SQL query editor';

interface SQLEditorPaneContentProps {
  actions: Actions;
  className?: string;
  isExpanded: boolean;
  isRunning: boolean;
  readOnly?: boolean;
  sqlQuery: string;
}

export function SQLEditorPaneContent({
  actions,
  className,
  isExpanded,
  isRunning,
  readOnly,
  sqlQuery,
}: SQLEditorPaneContentProps): JSX.Element {
  const {currentState: saveQueryState, makeCall: saveAndRunQuery} = useAsyncState({
    makeCall: actions.saveAndRunQuery,
  });

  function onRun(): void {
    // We want to ensure blocking running the query multiple times when one is already running
    if (!isRunning && saveQueryState.status !== AsyncStatus.LOADING) {
      saveAndRunQuery();
    }
  }

  return (
    <div role="group" aria-label={SQL_EDITOR_ARIA_LABEL}>
      <AutoSizer disableHeight={true}>
        {({width}) => (
          <SQLEditor
            className={className}
            onTextSelected={actions.setSQLSubQuery}
            onRun={onRun}
            height={isExpanded ? '500px' : `${BLOCK_EDITOR_TOP_ROW_HEIGHT}px`}
            onQueryChange={(newSQLQuery) => {
              actions.setSQLQuery(newSQLQuery);
            }}
            query={sqlQuery}
            width={`${width}px`}
            readOnly={readOnly}
            showGutter={true}
          />
        )}
      </AutoSizer>
    </div>
  );
}

export interface SQLEditorPaneProps {
  actions: Actions;
  additionalActionButtons?: JSX.Element;
  isExpanded: boolean;
  isRunning: boolean;
  sqlQuery: string;
}

const WAREHOUSE_STABILITY_LINK: string = 'https://docs.benchling.com/docs/stability';
// Keep in sync with benchling/warehouse/naming.py
const ALPHA_SUFFIX = '$alpha';
const BETA_SUFFIX = '$beta';

function doesSqlQueryReferenceAlphaOrBetaStability(sqlQuery: string): boolean {
  return sqlQuery.includes(ALPHA_SUFFIX) || sqlQuery.includes(BETA_SUFFIX);
}

export default function SQLEditorPane({
  actions,
  additionalActionButtons,
  isExpanded,
  isRunning,
  sqlQuery,
}: SQLEditorPaneProps): JSX.Element {
  const paneTitle: JSX.Element = (
    <span>
      <span>SQL</span>
      {doesSqlQueryReferenceAlphaOrBetaStability(sqlQuery) && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={
            <span>
              $alpha and $beta tables will change frequently. See{' '}
              <a href={WAREHOUSE_STABILITY_LINK} target="_blank" rel="noreferrer">
                here
              </a>{' '}
              for guidelines.
            </span>
          }
        />
      )}
    </span>
  );
  return (
    <Pane
      title={paneTitle}
      fitContent={true}
      toolbar={additionalActionButtons}
      dataTestAttrs={{component: 'SQLEditorPane', element: 'root'}}
    >
      <SQLEditorPaneContent
        actions={actions}
        isExpanded={isExpanded}
        isRunning={isRunning}
        sqlQuery={sqlQuery}
      />
    </Pane>
  );
}
